/**
 * This file contains the logic for generating and prefetching the baseline parallel report for export.
 *
 * The baseline parallel report for export is generated by running a quick-simulation with an extended planning horizon
 * and projected parallel orders (`project_parallel_orders`; see also `ReportLogic.generateParallelReportUuidWarning`).
 * It serves a distinct purpose from the main planning table short-horizon quick-simulation report used for rendering
 * the Gantt chart. Its primary use is in SAP/TXT export diff calculations, where an accurate baseline is needed for
 * comparison.
 *
 * Since the baseline parallel report is used only for export diff calculations, we can skip stock forecast calculations.
 * This is achieved by setting the `skip_stock` flag to true in the report parameters.
 *
 * The baseline parallel report is prefetched when the main Gantt report is ready. This way we avoid running two
 * quick-simulations simultaneously, which can reduce time to first render.
 */

import { deepClone } from "@/lib/util";
import { ReportLogic } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { SET_ERROR } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import {
    ADD_REPORT_TO_DELETE,
    UPDATE_PARALLEL_REPORT_ACTION_NAME,
    UPDATE_PARALLEL_REPORT_PARAMETERS,
} from "@/modules/pg/shared/planningTable/reducers/report/parallelReport";
import { SKIP_STOCK_REPORT } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";

import type { Dispatch, Store } from "redux";
import type { ReportParameters } from "@/modules/pg/shared/planningTable/reducers/common";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { GenerateParallelReportRet } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import type { SetErrorAction } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type {
    AddReportToDelete,
    UpdateParallelReportAction,
    UpdateParallelReportParameters,
} from "@/modules/pg/shared/planningTable/reducers/report/parallelReport";
import type { SetSkipStockReport } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import type { ExportBaselineParallelPrefetchSnapshot } from "@/modules/pg/shared/planningTable/lib/exportBaselineParallelReportPredicates";
import {
    areBaselineParallelPrefetchSnapshotsEqual,
    isBaselineParallelPrefetchContextUnchanged,
    shouldPrefetchBaselineParallelReportUuid,
    syntheticFirstPrefetchPrev,
} from "@/modules/pg/shared/planningTable/lib/exportBaselineParallelReportPredicates";

export type { ExportBaselineParallelPrefetchSnapshot } from "@/modules/pg/shared/planningTable/lib/exportBaselineParallelReportPredicates";
export {
    areBaselineParallelPrefetchSnapshotsEqual,
    arePlanningScopesEqual,
    isBaselineParallelPrefetchContextUnchanged,
    shouldPrefetchBaselineParallelReportUuid,
    shouldReconsiderBaselineParallelPrefetch,
    syntheticFirstPrefetchPrev,
} from "@/modules/pg/shared/planningTable/lib/exportBaselineParallelReportPredicates";

export type ExportBaselineParallelReportActions = SetSkipStockReport | UpdateParallelReportParameters | SetErrorAction;

/** Dispatch for parallel-export quick-sim side effects (skip_stock, params, error). */
export type ExportBaselineParallelReportDispatch = Dispatch<ExportBaselineParallelReportActions>;

type ExportBaselineParallelPrefetchDispatch = Dispatch<
    ExportBaselineParallelReportActions | UpdateParallelReportAction | AddReportToDelete
>;

/**
 * Reads Gantt + parallel slices into a prefetch snapshot.
 */
export function selectBaselineParallelPrefetchSnapshot(state: ReduxState): ExportBaselineParallelPrefetchSnapshot {
    const ganttReport = state.gantt_chart_report;
    const reportData = ganttReport.report_data;
    const parallelReport = state.gantt_chart_parallel_report;
    return {
        mainReportReady: reportData !== null && !ganttReport.report_loading,
        parallel_original_report_uuid: parallelReport.parallel_original_report_uuid,
        parameters: parallelReport.parameters,
    };
}

/**
 * For export diff, stock handling is forced to skip_stock; updates Redux so UI and sim stay aligned.
 * Returns the `ReportParameters` with `skip_stock` set to true.
 */
export function ensureSkipStockInReportParameters(
    dispatch: ExportBaselineParallelReportDispatch,
    params: ReportParameters
): ReportParameters {
    if (params.config.skip_stock) {
        return params;
    }

    const parameters = deepClone(params);
    parameters.config.skip_stock = true;

    dispatch({ type: SKIP_STOCK_REPORT, data: true });
    dispatch({ type: UPDATE_PARALLEL_REPORT_PARAMETERS, data: parameters });

    return parameters;
}

export type RunLongParallelQuickSimForExportOutcome = {
    result: GenerateParallelReportRet;
    parameters: ReportParameters;
};

/**
 * Runs the parallel long quick-sim used for export (SAP modal + TXT diff).
 * Dispatches `SET_ERROR` on failure.
 */
export async function runLongParallelQuickSimForExport(
    dispatch: ExportBaselineParallelReportDispatch,
    params: ReportParameters,
    initialReportUuid?: string | null
): Promise<RunLongParallelQuickSimForExportOutcome | undefined> {
    try {
        const parameters = ensureSkipStockInReportParameters(dispatch, params);
        const result = await ReportLogic.generateParallelReportUuidWarning(parameters, initialReportUuid);
        if (!result) {
            return undefined;
        }

        return { result, parameters };
    } catch (error) {
        dispatch({ type: SET_ERROR, data: (error as Error).message });
        return undefined;
    }
}

/**
 * Subscribes to the planning-table Redux store and prefetches `parallel_original_report_uuid` when
 * `shouldPrefetchBaselineParallelReportUuid` holds (deferred until main Gantt is ready).
 * Commits UUID + `ADD_REPORT_TO_DELETE` only after a stale check.
 *
 * After {@link destroy}, in-flight prefetch will not dispatch those commits (avoids updates after unmount).
 */
export class ExportBaselineParallelReportPrefetchSubscriber {
    store: Store<ReduxState>;
    unsubscribe: () => void;
    prevSnapshot: ExportBaselineParallelPrefetchSnapshot | null = null;
    isFetchingBaselineParallelReport = false;
    /**
     * When a prefetch is requested while another sim is in flight, we stash the latest parameters
     * and start that run from `finally` after the flag clears (missed retry if we only
     * advanced `prevSnapshot` and never got another store notification).
     */
    pendingBaselineParallelReportParameters: ReportParameters | null = null;
    destroyed = false;

    constructor(store: Store<ReduxState>) {
        this.store = store;
        this.unsubscribe = store.subscribe(this.handleStoreChange);
    }

    destroy(): void {
        this.destroyed = true;
        this.pendingBaselineParallelReportParameters = null;
        this.unsubscribe();
    }

    handleStoreChange = (): void => {
        const next = selectBaselineParallelPrefetchSnapshot(this.store.getState());
        const prev = this.prevSnapshot;

        if (prev === null) {
            this.prevSnapshot = next;
            void this.prefetchBaselineParallelReportIfNeeded(syntheticFirstPrefetchPrev(next), next);
            return;
        }

        if (areBaselineParallelPrefetchSnapshotsEqual(prev, next)) {
            return;
        }

        // Update before starting prefetch: prefetch → fetch → runLongParallelQuickSimForExport can
        // dispatch synchronously (skip_stock / parallel params), re-entering handleStoreChange. Assigning
        // prevSnapshot only after void prefetch could let the outer call overwrite a newer value set
        // during that nested run.
        this.prevSnapshot = next;
        void this.prefetchBaselineParallelReportIfNeeded(prev, next);
    };

    prefetchBaselineParallelReportIfNeeded = async (
        prev: ExportBaselineParallelPrefetchSnapshot,
        next: ExportBaselineParallelPrefetchSnapshot
    ): Promise<void> => {
        if (!shouldPrefetchBaselineParallelReportUuid(prev, next)) {
            return;
        }

        const reportParameters = next.parameters;
        if (reportParameters === null) {
            return;
        }

        await this.fetchBaselineParallelReportUuid(reportParameters);
    };

    fetchBaselineParallelReportUuid = async (reportParameters: ReportParameters): Promise<void> => {
        if (this.destroyed) {
            return;
        }

        if (this.isFetchingBaselineParallelReport) {
            this.pendingBaselineParallelReportParameters = reportParameters;
            return;
        }

        this.isFetchingBaselineParallelReport = true;
        const dispatch = this.store.dispatch as ExportBaselineParallelPrefetchDispatch;

        try {
            const outcome = await runLongParallelQuickSimForExport(dispatch, reportParameters);
            if (this.destroyed) {
                return;
            }

            const reportUuid = outcome?.result.report_uuid;
            if (!reportUuid) {
                return;
            }

            const contextAfter = selectBaselineParallelPrefetchSnapshot(this.store.getState());
            if (!isBaselineParallelPrefetchContextUnchanged(contextAfter, reportParameters)) {
                return;
            }

            // Reducer overwrites parallel_original_report_uuid on each dispatch so a newer pending
            // prefetch result can replace an older baseline (see parallelReportReducer).
            dispatch({ type: UPDATE_PARALLEL_REPORT_ACTION_NAME, data: reportUuid });
            dispatch({ type: ADD_REPORT_TO_DELETE, data: reportUuid });
        } finally {
            this.isFetchingBaselineParallelReport = false;
            const pending = this.pendingBaselineParallelReportParameters;
            this.pendingBaselineParallelReportParameters = null;
            if (pending !== null && !this.destroyed) {
                void this.fetchBaselineParallelReportUuid(pending);
            }
        }
    };
}
